import { StudentForm } from 'components/UserForm'
import { getUpdateValues } from 'components/UserForm/StudentForm'
import { TeacherProfile } from 'components/TeacherProfile'
import {
  STUDENT_DC,
  STUDENT_UPDATE,
  TEACHER_DC,
  TEACHER_UPDATE,
  TRAINING_UPDATE,
} from '../../constants'
import { RefObject, useCallback, useEffect, useState } from 'react'
import {
  AnyUserData,
  ConfigUtils,
  StudentData,
  TeacherData,
  UserUpdateFormValues,
  ViewMode,
} from 'types'
import { TabbedDisplay } from '../TabbedDisplay'
import { FormikProps } from 'formik'
import { Box, Flex, Heading } from 'theme-ui'
import { useList, useUser } from 'hooks/userHooks'
import CopyBtn from 'components/CopyBtn'
import { useViewSwitchingForm } from 'hooks/useViewSwitchingForm'
import { UserList } from 'components/UserList'
import { TrainingTab } from 'components/TeacherProfile/TrainingTab'
import { RefresherTab } from 'components/TeacherProfile/RefresherTab'
import { DCQuestionnaires } from 'components/StudentProfile/DCQuestionnaires'

const Profile = (props: {
  pageContext:
    | typeof STUDENT_UPDATE
    | typeof TEACHER_UPDATE
    | typeof TRAINING_UPDATE
  user: AnyUserData
  teacher?: TeacherData
  loading?: boolean
  isSupport?: boolean
  configUtils: ConfigUtils
  viewMode: ViewMode
  formikRef: RefObject<FormikProps<UserUpdateFormValues>>
  responseError?: string
  disabled: boolean
  isUserUsingNewOrgs: boolean
  setTeacherId: (id: string) => void
  onEnterEditing: () => void
  onLeaveForm: () => void
  onSubmitForm: (formData: Partial<UserUpdateFormValues>) => void
  onCancelEditing: () => void
  access?: {
    viewDropdownDate: boolean
  }
}) => {
  const { user, pageContext } = props
  if (pageContext === STUDENT_UPDATE) {
    return <StudentForm {...props} user={user as StudentData} />
  } else {
    return <TeacherProfile {...props} user={user as TeacherData} />
  }
}

export const UserProfile = (props: {
  user: AnyUserData
  pageContext:
    | typeof TEACHER_UPDATE
    | typeof STUDENT_UPDATE
    | typeof TRAINING_UPDATE
  configUtils: ConfigUtils
  isSupport?: boolean
  responseError?: string
  loading?: boolean
  initialView?: ViewMode
  onLeaveForm: () => void
  onSubmitForm: (formData: Partial<UserUpdateFormValues>) => void
  access?: {
    viewDropdownDate: boolean
  }
}) => {
  const {
    id,
    userType,
    defaultTeachingOrgId,
    teachingOrgId,
    orgId,
  } = props.user

  //3730 clean up
  const [isUserUsingNewOrgs, setIsUserUsingNewOrgs] = useState<boolean>(
    !!defaultTeachingOrgId || !!teachingOrgId || !!orgId
  )

  useEffect(() => {
    setIsUserUsingNewOrgs(!!teachingOrgId || !!defaultTeachingOrgId || !!orgId)
  }, [setIsUserUsingNewOrgs, teachingOrgId, defaultTeachingOrgId, orgId])

  const [teacherId, setTeacherId] = useState('')

  useEffect(() => {
    if (props.user?.teacherID) setTeacherId(props.user.teacherID)
  }, [props.user?.teacherID, setTeacherId])
  const [teacher] = useUser(teacherId)
  const useListOutput = useList({ teacherId: id })
  const listUsers = useListOutput.list

  useEffect(() => {
    if (id && (userType === 'TeacherDC' || userType === 'Teacher')) {
      listUsers({ teacherId: id })
    }
  }, [id, userType, listUsers])

  const onCancelEditing = useCallback(
    ({ values }: FormikProps<UserUpdateFormValues>) => {
      setTeacherId && setTeacherId(values.teacherID ?? '')
    },
    [setTeacherId]
  )

  const incompleteTeacher =
    props.pageContext === TEACHER_UPDATE && !props.user.contact?.default?.email
  const startInEditMode = !!incompleteTeacher || props.initialView === 'edit'
  const {
    formikRef,
    handleEnterEditing,
    handleCancelEditing,
    handleSubmitForm,
    handleLeaveForm,
    viewMode,
  } = useViewSwitchingForm<UserUpdateFormValues>({
    getChanges: getUpdateValues,
    onSubmitForm: props.onSubmitForm,
    onCancelEditing,
    onLeaveForm: props.onLeaveForm,
    initialView: startInEditMode ? 'edit' : 'view',
  })

  useEffect(() => {
    if (props.responseError) {
      handleEnterEditing()
    }
  }, [props.responseError, handleEnterEditing])

  const isDisabledStatus = props.user.status === 'disabled' && !props.isSupport

  const disabled =
    isDisabledStatus ||
    (props.pageContext === STUDENT_UPDATE && props.user.userType === TEACHER_DC)

  const formProps = {
    teacher,
    viewMode,
    disabled,
    responseError: props.responseError,
    onEnterEditing: handleEnterEditing,
    onSubmitForm: handleSubmitForm,
    onLeaveForm: handleLeaveForm,
    onCancelEditing: handleCancelEditing,
    setTeacherId: setTeacherId,
    formikRef,
    isUserUsingNewOrgs,
    pageContext: props.pageContext,
    user: props.user,
    configUtils: props.configUtils,
    isSupport: props.isSupport,
    loading: props.loading,
    access: props.access,
  }
  const showRefresherTab = !!props?.user?.lmsRefresherCourseData
  const trainingTabProps = {
    viewMode,
    user: props.user as TeacherData,
  }
  const trainingTabDisplay = showRefresherTab ? (
    <RefresherTab {...trainingTabProps} />
  ) : (
    <TrainingTab {...trainingTabProps} />
  )
  const tabs = [
    {
      title: 'Profile',
      key: 'form',
      display: <Profile {...formProps} />,
    },
  ]

  const showTrainingTab =
    !!props?.user?.lmsCourseData || !!props?.user?.lmsRefresherCourseData
  if (
    showTrainingTab &&
    (props.pageContext === TRAINING_UPDATE ||
      props.pageContext === TEACHER_UPDATE)
  ) {
    tabs.push({
      title: 'Training',
      key: 'training_details',
      display: trainingTabDisplay,
    })
  }
  if (
    props.isSupport &&
    (props.pageContext === TEACHER_UPDATE ||
      props.pageContext === TRAINING_UPDATE)
  ) {
    tabs.push({
      title: 'Student list',
      key: 'student_list',
      display: <UserList {...useListOutput} />,
    })
  }
  if (
    props.user.userType === STUDENT_DC &&
    props.user.hasOwnProperty('reflectionAnswers') &&
    props.user.reflectionAnswers !== null
  ) {
    tabs.push({
      title: 'Reflection Questions',
      key: 'reflection_questions',
      display: <DCQuestionnaires {...props.user.reflectionAnswers} />,
    })
  }
  const [currTab, setCurrTab] = useState<string>(
    !props.isSupport &&
      props.pageContext === TRAINING_UPDATE &&
      props.user.status !== 'completed'
      ? 'training_details'
      : 'form'
  )
  const handleChangeTabs = useCallback(
    async (newTabKey: string) => {
      if (await handleCancelEditing()) {
        setCurrTab(newTabKey)
      }
    },
    [handleCancelEditing, setCurrTab]
  )
  return (
    <Box sx={{ width: '100%' }} data-cy="user-profile">
      <Flex variant={`userProfile.${currTab}`}>
        <Box sx={{ py: 30 }}>
          <Flex sx={{ flexWrap: 'wrap' }}>
            <Heading variant="forms.h1">
              {`${props.user.forename} ${props.user.surname}`}
            </Heading>
            {props.isSupport && (
              <CopyBtn
                sx={{
                  ml: 2,
                  mt: 3,
                }}
                copyText={props.user.id}
                copyTextName="User ID"
              />
            )}
          </Flex>
        </Box>
        <TabbedDisplay
          displays={tabs}
          selectedKey={currTab}
          onSelectionChange={handleChangeTabs}
        />
      </Flex>
    </Box>
  )
}
